import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Dropdown,
  Empty,
  Form,
  Radio,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import {Button} from '@dabafinance/react-components';
import moment from 'moment';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {ViewUserDocument} from 'pages/adminUser/userDetails/tabs/documents/viewDocument';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import backIcon from 'images/back.png';
import dotsIcon from 'icons/dots.svg';
import {
  CONFIRM_CHANGES,
  DELETE_SOURCE,
  GENERATE_REPORT,
  GET_PICKS,
  REMOVE_PICK,
  TRANSLATE_PICKS,
} from '../resolvers';
import {
  MAX_PICKS_PER_REPORT,
  NO_TEMPLATE_MESSAGE,
  PICK_TYPES,
  canGenerateReport,
  errorMessage,
  formatPrice,
  isAnalysisEmpty,
  isGqlError,
  languageLabel,
  moveLabel,
  otherLanguage,
  projectedMove,
  readMutation,
  sanitizeAnalysis,
  sourceLabel,
  typeLabel,
} from '../constants';
import {useRecommendationRow} from './useRecommendationRow';
import {useReportPolling} from './useReportPolling';
import {useDirtyRecommendationStore} from './dirtyStore';
import {SinglePickModal} from './SinglePickModal';
import {SourcePicksModal} from './SourcePicksModal';
import {PublishModal} from './PublishModal';
import styles from './workspace.module.scss';

const plainText = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The recommendation workspace, scoped by recommendation + source + type.
 *
 * Type is always sent, and so is source — the schema marks source optional, but
 * omitting it silently falls back to the parent default and makes the tabs lie.
 */
export const RecommendationWorkspace = () => {
  const {recommendationId} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [pickForm] = Form.useForm();
  const [sourceForm] = Form.useForm();

  const {
    recommendation,
    loading: rowLoading,
    error: rowError,
    refresh,
  } = useRecommendationRow(recommendationId);

  const [source, setSource] = useState(null);
  const [type, setType] = useState('BUY');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pickModal, setPickModal] = useState({open: false, pick: null});
  const [sourceModal, setSourceModal] = useState({open: false, mode: null});
  const [publishModal, setPublishModal] = useState(false);
  const [reportModal, setReportModal] = useState({open: false, report: null});
  const [removeModal, setRemoveModal] = useState({open: false, pick: null});
  const [analysisModal, setAnalysisModal] = useState({
    open: false,
    pick: null,
    language: null,
  });
  const [deleteSourceModal, setDeleteSourceModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.InstrumentRecommendations),
  );
  const {markDirty, clearDirty, dirty} = useDirtyRecommendationStore();

  const availableSources = useMemo(
    () => recommendation?.availableSources || [],
    [recommendation],
  );

  // The default source is only trustworthy when it is actually registered —
  // deleting a default can leave the parent pointing at a source that is gone.
  const defaultSourceMissing = Boolean(
    recommendation?.defaultSource &&
      availableSources.length &&
      !availableSources.some(
        entry => entry.name === recommendation.defaultSource,
      ),
  );

  useEffect(() => {
    if (source || !availableSources.length) return;
    const preferred = availableSources.some(
      entry => entry.name === recommendation?.defaultSource,
    )
      ? recommendation.defaultSource
      : availableSources[0].name;
    setSource(preferred);
  }, [availableSources, recommendation, source]);

  useEffect(() => {
    if (location.state?.openPublish) setPublishModal(true);
  }, [location.state]);

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery(GET_PICKS, {
    variables: {recommendationId, source, type, page, limit},
    skip: !source,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const result = data?.getInstrumentRecommendationPicks;
  const unionError = isGqlError(result) ? result : null;
  const picks = unionError ? [] : result?.data?.instrumentRecommendation || [];
  const reports = unionError ? [] : result?.data?.reports || [];
  const total = unionError ? 0 : result?.pagination?.total || 0;

  const {reportState, startReportWatch, clearReportWatch} =
    useReportPolling(refetch);

  const [removePick, {loading: removing}] = useMutation(REMOVE_PICK, {
    errorPolicy: 'all',
  });
  const [translatePicks, {loading: translating}] = useMutation(
    TRANSLATE_PICKS,
    {errorPolicy: 'all'},
  );
  const [generateReport, {loading: generating}] = useMutation(GENERATE_REPORT, {
    errorPolicy: 'all',
  });
  const [confirmChanges, {loading: confirming}] = useMutation(CONFIRM_CHANGES, {
    errorPolicy: 'all',
  });
  const [deleteSource, {loading: deletingSource}] = useMutation(DELETE_SOURCE, {
    errorPolicy: 'all',
  });

  const isPublished = Boolean(recommendation?.isPublished);
  const currency = recommendation?.exchange?.baseCurrencyCode;
  const baseLanguage = picks[0]?.language || recommendation?.language || 'EN';
  const targetLanguage = otherLanguage(baseLanguage);
  const isDirty = Boolean(dirty[`${recommendationId}|${source}|${type}`]);

  /** A published edit is invisible to users until changes are confirmed. */
  const afterPickWrite = () => {
    refetch();
    if (isPublished) markDirty(recommendationId, source, type);
  };

  const untranslated = picks.filter(
    pick =>
      !pick.multiLang?.some(
        entry =>
          entry.language === otherLanguage(pick.language) &&
          !isAnalysisEmpty(entry.analysis),
      ),
  );
  const incomplete = picks.filter(
    pick =>
      !(pick.priceAsOf > 0) ||
      !pick.priceAsOfDate ||
      !(pick.projectedPriceBefore > 0) ||
      !pick.projectedPriceBeforeDate ||
      isAnalysisEmpty(pick.analysis),
  );

  const reportBlockReason = !canGenerateReport(type)
    ? NO_TEMPLATE_MESSAGE
    : !picks.length
    ? 'Add picks before generating a report'
    : total > MAX_PICKS_PER_REPORT
    ? `This group has ${total} picks — reports read at most ${MAX_PICKS_PER_REPORT}`
    : incomplete.length
    ? `${incomplete.length} picks are missing prices, dates or analysis`
    : untranslated.length
    ? `${untranslated.length} picks have no ${languageLabel(
        targetLanguage,
      )} analysis`
    : null;

  const handleRemovePick = () => {
    removePick({
      variables: {input: {recommendationId, pickId: removeModal.pick?.id}},
    })
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'removeInstrumentRecommendationPick',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        setRemoveModal({open: false, pick: null});
        afterPickWrite();
      })
      .catch(error => toast.error(error?.message));
  };

  const handleTranslate = () => {
    translatePicks({
      variables: {recommendationId, targetLanguage, source, type},
    })
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'translateInstrumentRecommendationPicks',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        refetch();
        // Translation kicks off report generation for this source/type.
        if (canGenerateReport(type))
          startReportWatch({hasExistingReport: reports.length > 0});
      })
      .catch(error => toast.error(error?.message));
  };

  const handleGenerateReport = () => {
    generateReport({variables: {recommendationId, source, type}})
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'generateInstrumentRecommendationReport',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        setGenerateModal(false);
        startReportWatch({hasExistingReport: reports.length > 0});
      })
      .catch(error => toast.error(error?.message));
  };

  const handleConfirmChanges = () => {
    confirmChanges({variables: {input: {recommendationId, source, type}}})
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'confirmInstrumentRecommendationChanges',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        setConfirmModal(false);
        clearDirty(recommendationId, source, type);
        if (canGenerateReport(type))
          startReportWatch({hasExistingReport: reports.length > 0});
      })
      .catch(error => toast.error(error?.message));
  };

  const handleDeleteSource = () => {
    deleteSource({variables: {recommendationId, source}})
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'deleteInstrumentRecommendationPickSource',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        setDeleteSourceModal(false);
        setSource(null);
        refresh();
      })
      .catch(error => toast.error(error?.message));
  };

  const columns = [
    {title: '#', dataIndex: 'order', width: 56},
    {
      title: 'Instrument',
      dataIndex: 'instrument',
      width: 260,
      render: (text, record) => {
        const logo = record.issuer?.logoImgURL || record.instrument?.icon;
        return (
          <div className={styles.instrumentCell}>
            {logo && (
              <img src={logo} alt="" className={styles.instrumentLogo} />
            )}
            <div className={styles.nameCell}>
              <span className={styles.primaryText}>
                {record.instrument?.name || record.instrumentId}
              </span>
              <span className={styles.mutedText}>
                {[record.instrument?.ticker, record.issuer?.name]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Exchange',
      dataIndex: 'exchange',
      width: 110,
      render: (text, record) => (
        <Tag className={styles.softTag}>{record.exchange?.code}</Tag>
      ),
    },
    {
      title: 'Price as of',
      dataIndex: 'priceAsOf',
      width: 150,
      render: (priceAsOf, record) => (
        <div className={styles.nameCell}>
          <span className={styles.numValue}>
            {formatPrice(priceAsOf, currency)}
          </span>
          <span className={styles.subText}>
            {record.priceAsOfDate
              ? moment(record.priceAsOfDate).format('DD MMM YYYY')
              : '-'}
          </span>
        </div>
      ),
    },
    {
      title: 'Target',
      dataIndex: 'projectedPriceBefore',
      width: 170,
      render: (projected, record) => {
        const move = projectedMove(record.priceAsOf, projected);
        return (
          <div className={styles.nameCell}>
            <span className={styles.numValue}>
              {formatPrice(projected, currency)}
            </span>
            <span className={styles.subText}>
              {record.projectedPriceBeforeDate
                ? moment(record.projectedPriceBeforeDate).format('DD MMM YYYY')
                : '-'}
            </span>
            {move !== null && (
              <span className={styles.subText}>
                {moveLabel(record.type)}: {move.toFixed(2)}%
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Analysis',
      dataIndex: 'analysis',
      width: 240,
      render: (analysis, record) => {
        const text = plainText(analysis);
        if (!text) return <span className={styles.mutedText}>—</span>;
        // A raw tooltip on a multi-page analysis takes over the screen, so cap
        // the hover to a short snippet and open the full text in a modal.
        return (
          <Tooltip
            title={`${text.slice(0, 160)}${text.length > 160 ? '…' : ''}`}
            overlayStyle={{maxWidth: 320}}>
            <span
              className={styles.analysisPreview}
              onClick={() =>
                setAnalysisModal({
                  open: true,
                  pick: record,
                  language: record.language,
                })
              }>
              {text.slice(0, 120)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Languages',
      dataIndex: 'language',
      width: 130,
      render: (language, record) => {
        const translated = record.multiLang?.some(
          entry =>
            entry.language === otherLanguage(language) &&
            !isAnalysisEmpty(entry.analysis),
        );
        return (
          <div className={styles.chips}>
            <Tag className={styles.softTag}>{languageLabel(language)}</Tag>
            {translated ? (
              <Tag color="green">{languageLabel(otherLanguage(language))}</Tag>
            ) : (
              <Tag color="orange">Not translated</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      fixed: 'right',
      render: (text, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit pick',
                disabled: !tabOperations?.update,
                onClick: () => setPickModal({open: true, pick: record}),
              },
              {
                key: 'remove',
                label: 'Remove pick',
                danger: true,
                disabled: !tabOperations?.delete,
                onClick: () => setRemoveModal({open: true, pick: record}),
              },
            ],
          }}>
          <img src={dotsIcon} alt="actions" className={styles.dotsIcon} />
        </Dropdown>
      ),
    },
  ];

  if (rowLoading && !recommendation)
    return (
      <div className={styles.centered}>
        <Spin />
      </div>
    );

  if (rowError && !recommendation)
    return (
      <div className={styles.container}>
        <Alert
          type="error"
          showIcon
          message={rowError}
          action={
            <Button
              label="Back to list"
              type="secondary"
              onClick={() => navigate('/dashboard/stock-recommendations')}
            />
          }
        />
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <img
            src={backIcon}
            alt="back"
            className={styles.backIcon}
            onClick={() => navigate('/dashboard/stock-recommendations')}
          />
          <div>
            <h1>{recommendation?.name}</h1>
            <div className={styles.chips}>
              <Tooltip title={recommendation?.exchange?.stockExchangeName}>
                <Tag className={styles.softTag}>
                  {recommendation?.exchange?.code}
                  {currency ? ` · ${currency}` : ''}
                </Tag>
              </Tooltip>
              <Tag color={isPublished ? 'green' : 'default'}>
                {isPublished ? 'Published' : 'Draft'}
              </Tag>
              <Tag className={styles.softTag}>
                {recommendation?.date
                  ? moment(recommendation.date).format('DD MMM YYYY')
                  : '-'}
              </Tag>
              <Tag className={styles.softTag}>
                {languageLabel(recommendation?.language)}
              </Tag>
              <Tag className={styles.softTag}>Public Equity</Tag>
            </div>
          </div>
        </div>

        <div className={styles.headerActions}>
          {/* Registering a source is additive — it leaves every source already
              published untouched — so it stays available after publication.
              The new source's picks are held behind the same confirm-and-notify
              gate as any other post-publication edit. */}
          <Button
            label="Add source"
            type="secondary"
            disabled={!tabOperations?.create}
            onClick={() => setSourceModal({open: true, mode: 'NEW_SOURCE'})}
          />
          {!isPublished && (
            <Button
              label="Review and publish"
              disabled={!tabOperations?.update || !availableSources.length}
              onClick={() => setPublishModal(true)}
            />
          )}
        </div>
      </div>

      {defaultSourceMissing && (
        <Alert
          className={styles.alert}
          type="warning"
          showIcon
          message={`The default source (${sourceLabel(
            recommendation?.defaultSource,
          )}) is not in this recommendation's sources. Submit a pick set with "Make this the default source" to fix it.`}
        />
      )}

      {isPublished && isDirty && (
        <Alert
          className={styles.alert}
          type="warning"
          showIcon
          message="This published recommendation has unconfirmed changes."
          description={`${sourceLabel(source)} · ${typeLabel(
            type,
          )} was edited after publication. Users do not see the update until it is confirmed.`}
          action={
            <Button
              label="Confirm and notify"
              disabled={!tabOperations?.update}
              onClick={() => setConfirmModal(true)}
            />
          }
        />
      )}

      {!availableSources.length ? (
        <div className={styles.emptyState}>
          <Empty description="No sources yet — a recommendation needs at least one source with picks." />
          <Button
            label="Add source and picks"
            disabled={!tabOperations?.create}
            onClick={() => setSourceModal({open: true, mode: 'NEW_SOURCE'})}
          />
        </div>
      ) : (
        <>
          <Tabs
            activeKey={source || undefined}
            onChange={nextSource => {
              setSource(nextSource);
              setPage(1);
              clearReportWatch();
            }}
            items={availableSources.map(entry => ({
              key: entry.name,
              label: (
                <span className={styles.sourceTab}>
                  {entry.imageUrl && <img src={entry.imageUrl} alt="" />}
                  {sourceLabel(entry.name)}
                  {entry.name === recommendation?.defaultSource && (
                    <Tag className={styles.softTag}>Default</Tag>
                  )}
                </span>
              ),
            }))}
          />

          <div className={styles.toolbar}>
            <Radio.Group
              optionType="button"
              value={type}
              options={PICK_TYPES.map(pickType => ({
                label: typeLabel(pickType),
                value: pickType,
              }))}
              onChange={event => {
                setType(event.target.value);
                setPage(1);
                clearReportWatch();
              }}
            />

            <div className={styles.toolbarActions}>
              <Button
                label="Add pick"
                type="secondary"
                disabled={!tabOperations?.create}
                onClick={() => setPickModal({open: true, pick: null})}
              />
              <Button
                label={`Bulk upsert ${typeLabel(type)} picks`}
                type="secondary"
                disabled={!tabOperations?.create || isPublished}
                onClick={() => setSourceModal({open: true, mode: 'BULK'})}
              />
              <Button
                label={
                  translating
                    ? 'Translating…'
                    : `Translate missing analyses to ${languageLabel(
                        targetLanguage,
                      )}`
                }
                type="secondary"
                disabled={
                  !tabOperations?.create || translating || !picks.length
                }
                onClick={handleTranslate}
              />
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    ...reports.map((report, index) => ({
                      key: `report-${index}`,
                      label: `Open ${languageLabel(report.language)} PDF`,
                      onClick: () => setReportModal({open: true, report}),
                    })),
                    ...(reports.length
                      ? []
                      : [
                          {
                            key: 'none',
                            label:
                              'No generated reports for this source and type',
                            disabled: true,
                          },
                        ]),
                    {
                      key: 'generate',
                      label: reportBlockReason
                        ? reportBlockReason
                        : reports.length
                        ? 'Regenerate report'
                        : 'Generate report',
                      disabled:
                        Boolean(reportBlockReason) || !tabOperations?.update,
                      onClick: () => setGenerateModal(true),
                    },
                  ],
                }}>
                <div>
                  <Button label="Reports" type="secondary" />
                </div>
              </Dropdown>
              {!isPublished && (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'delete-source',
                        label: 'Delete this source',
                        danger: true,
                        // Deleting the current default while other sources
                        // remain would leave the parent pointing at nothing —
                        // the backend only recalculates when one source is left.
                        disabled:
                          !tabOperations?.delete ||
                          (source === recommendation?.defaultSource &&
                            availableSources.length > 1),
                        onClick: () => setDeleteSourceModal(true),
                      },
                    ],
                  }}>
                  <img
                    src={dotsIcon}
                    alt="source actions"
                    className={styles.dotsIcon}
                  />
                </Dropdown>
              )}
            </div>
          </div>

          {reportState.status !== 'idle' && (
            <Alert
              className={styles.alert}
              type={reportState.status === 'ready' ? 'success' : 'info'}
              showIcon
              closable
              onClose={clearReportWatch}
              message={reportState.message}
            />
          )}

          {(unionError || queryError) && (
            <Alert
              className={styles.alert}
              type="error"
              showIcon
              message={errorMessage(
                unionError,
                queryError?.message || 'Could not load picks',
              )}
              action={
                <Button label="Retry" type="secondary" onClick={refetch} />
              }
            />
          )}

          <Table
            rowKey="id"
            className={styles.table}
            loading={loading}
            columns={columns}
            dataSource={picks}
            scroll={{x: 1080}}
            locale={{
              emptyText: (
                <Empty
                  description={`No ${typeLabel(type)} picks for ${sourceLabel(
                    source,
                  )}`}
                />
              ),
            }}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              showTotal: count => `${count} picks`,
              onChange: (nextPage, nextLimit) => {
                setPage(nextPage);
                setLimit(nextLimit);
              },
            }}
          />
          <p className={styles.mutedText}>
            Picks are ordered by the backend and reordering is not supported.
          </p>
        </>
      )}

      <DBModal
        isOpen={pickModal.open}
        width={900}
        destroyOnClose
        handleClose={() => {
          setPickModal({open: false, pick: null});
          pickForm.resetFields();
        }}
        content={
          <SinglePickModal
            form={pickForm}
            recommendation={recommendation}
            source={source}
            type={type}
            pick={pickModal.pick}
            onSaved={afterPickWrite}
            onClose={() => {
              setPickModal({open: false, pick: null});
              pickForm.resetFields();
            }}
          />
        }
      />

      <DBModal
        isOpen={sourceModal.open}
        width={1000}
        destroyOnClose
        handleClose={() => {
          setSourceModal({open: false, mode: null});
          sourceForm.resetFields();
        }}
        content={
          <SourcePicksModal
            form={sourceForm}
            recommendation={recommendation}
            mode={sourceModal.mode}
            source={sourceModal.mode === 'BULK' ? source : undefined}
            onSaved={async ({source: savedSource, type: savedType}) => {
              await refresh();
              setSource(savedSource);
              setType(savedType);
              refetch();
              // Users do not see a post-publication addition until it is
              // confirmed, so surface it the way an edited pick is surfaced.
              if (isPublished)
                markDirty(recommendationId, savedSource, savedType);
            }}
            onClose={() => {
              setSourceModal({open: false, mode: null});
              sourceForm.resetFields();
            }}
          />
        }
      />

      <DBModal
        isOpen={publishModal}
        width={720}
        destroyOnClose
        handleClose={() => setPublishModal(false)}
        content={
          <PublishModal
            recommendation={recommendation}
            onClose={() => setPublishModal(false)}
            onPublished={refresh}
          />
        }
      />

      <DBModal
        isOpen={reportModal.open}
        width={1000}
        destroyOnClose
        handleClose={() => setReportModal({open: false, report: null})}
        content={
          <ViewUserDocument
            url={reportModal.report?.url}
            documentName={`${sourceLabel(source)} ${typeLabel(
              type,
            )} — ${languageLabel(reportModal.report?.language)} report`}
          />
        }
      />

      <DBModal
        isOpen={analysisModal.open}
        width={760}
        destroyOnClose
        handleClose={() =>
          setAnalysisModal({open: false, pick: null, language: null})
        }
        content={
          <AnalysisView
            pick={analysisModal.pick}
            initialLanguage={analysisModal.language}
          />
        }
      />

      <ConfirmModal
        openModal={removeModal.open}
        setOpenModal={() => setRemoveModal({open: false, pick: null})}
        title="Remove pick"
        loading={removing}
        handleOperation={handleRemovePick}
        confirmText={
          <span>
            Remove <strong>{removeModal.pick?.instrument?.name}</strong> (
            {removeModal.pick?.instrument?.ticker}) from{' '}
            <strong>{sourceLabel(source)}</strong> {typeLabel(type)}? Its
            translations are deleted and the remaining picks are renumbered.
          </span>
        }
      />

      <ConfirmModal
        openModal={generateModal}
        setOpenModal={setGenerateModal}
        title={reports.length ? 'Regenerate report' : 'Generate report'}
        loading={generating}
        handleOperation={handleGenerateReport}
        confirmText={
          <span>
            Generate the {languageLabel('EN')} and {languageLabel('FR')} PDFs
            for <strong>{sourceLabel(source)}</strong> {typeLabel(type)}.
            Generation runs in the background — the link appears once it
            finishes.
          </span>
        }
      />

      <ConfirmModal
        openModal={confirmModal}
        setOpenModal={setConfirmModal}
        title="Confirm changes and notify"
        loading={confirming}
        handleOperation={handleConfirmChanges}
        confirmText={
          <span>
            This regenerates the reports for{' '}
            <strong>{sourceLabel(source)}</strong> {typeLabel(type)} and sends a
            recommendation-update notification to users. Make sure every change
            for this group is finished.
          </span>
        }
      />

      <ConfirmModal
        openModal={deleteSourceModal}
        setOpenModal={setDeleteSourceModal}
        title="Delete source"
        loading={deletingSource}
        handleOperation={handleDeleteSource}
        confirmText={
          <span>
            Delete <strong>{sourceLabel(source)}</strong> and every one of its
            Buy, Hold and Sell picks from this recommendation? This cannot be
            undone.
          </span>
        }
      />
    </div>
  );
};

/**
 * Read-only view of a pick's analysis, rendered as sanitized HTML rather than
 * the plain-text tooltip — which for a multi-page analysis covered the screen.
 * Offers the translated version too when one exists.
 */
const AnalysisView = ({pick, initialLanguage}) => {
  const base = pick?.language;
  const translation = pick?.multiLang?.find(
    entry => entry.language === otherLanguage(base) && entry.analysis,
  );
  const [language, setLanguage] = useState(initialLanguage || base);

  const html =
    language === base
      ? pick?.analysis
      : pick?.multiLang?.find(entry => entry.language === language)?.analysis;

  return (
    <div className={styles.modal}>
      <h1>Analysis</h1>
      <p className={styles.modalSubtitle}>
        {pick?.instrument?.name || pick?.instrumentId}
        {pick?.instrument?.ticker ? ` · ${pick.instrument.ticker}` : ''}
      </p>

      {translation && (
        <div className={styles.languageRow}>
          <Radio.Group
            optionType="button"
            value={language}
            options={[base, otherLanguage(base)].map(code => ({
              label: languageLabel(code),
              value: code,
            }))}
            onChange={event => setLanguage(event.target.value)}
          />
        </div>
      )}

      {isAnalysisEmpty(html) ? (
        <Empty
          description={`No ${languageLabel(language)} analysis for this pick`}
        />
      ) : (
        <div
          className={styles.analysisBody}
          // Sanitized on write and again here before rendering.
          dangerouslySetInnerHTML={{__html: sanitizeAnalysis(html)}}
        />
      )}
    </div>
  );
};

export default RecommendationWorkspace;
